import { ChevronDown, X, type LucideIcon } from 'lucide-react';
import { useId } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface MultiSelectOption<V extends number | string = number> {
  value: V;
  label: string;
  /** 선택지 앞 아이콘(예: 사업 유형). */
  icon?: LucideIcon;
  /** 선택지 뒤 건수(필터 facet) — 없으면 표시하지 않는다. */
  count?: number;
}

interface MultiSelectPopoverProps<V extends number | string> {
  options: MultiSelectOption<V>[];
  value: V[];
  onChange: (next: V[]) => void;
  placeholder?: string;
  className?: string;
  /** 접근성 라벨(라벨 요소가 별도로 있을 때 id 연결) */
  'aria-labelledby'?: string;
}

/**
 * 다중 선택(체크박스 목록) — AntD `Select mode="multiple"` 대체. shadcn Select 는 단일 선택뿐이라
 * Popover + Checkbox 로 구성한다. 트리거는 첫 선택 라벨 + "+n" 으로 요약하고, 지우기(X)로 전체 해제.
 * 값은 숫자(분류 노드 id)·문자열(지역·코드) 모두 받는다.
 */
export function MultiSelectPopover<V extends number | string>({
  options,
  value,
  onChange,
  placeholder = '전체',
  className,
  'aria-labelledby': labelledBy,
}: MultiSelectPopoverProps<V>) {
  const idPrefix = useId();
  const selected = options.filter((o) => value.includes(o.value));
  const summary =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? selected[0].label
        : `${selected[0].label} +${selected.length - 1}`;

  function toggle(v: V, checked: boolean) {
    onChange(checked ? [...value, v] : value.filter((x) => x !== v));
  }

  return (
    <Popover>
      <div className={cn('relative', className)}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-labelledby={labelledBy}
            className={cn(
              'w-full justify-between pr-8 font-normal',
              selected.length === 0 ? 'text-muted-foreground' : 'text-foreground',
            )}
          >
            <span className="truncate">{summary}</span>
            {selected.length === 0 && <ChevronDown className="size-4 shrink-0 opacity-50" />}
          </Button>
        </PopoverTrigger>
        {selected.length > 0 && (
          <button
            type="button"
            aria-label="선택 지우기"
            className="absolute top-1/2 right-2 -translate-y-1/2 rounded-sm text-muted-foreground hover:text-foreground"
            onClick={() => onChange([])}
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
      <PopoverContent align="start" className="w-64 p-1.5">
        <ul className="flex max-h-64 flex-col overflow-y-auto">
          {options.map((o, i) => {
            const checked = value.includes(o.value);
            const id = `${idPrefix}-${i}`;
            return (
              <li key={o.value}>
                <label
                  htmlFor={id}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                >
                  <Checkbox
                    id={id}
                    checked={checked}
                    onCheckedChange={(c) => toggle(o.value, c === true)}
                  />
                  {o.icon && (
                    <o.icon
                      className="size-3.5 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                  <span className="truncate">{o.label}</span>
                  {o.count !== undefined && (
                    <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
                      {formatNumber(o.count)}
                    </span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
